// src/components/Onboarding.jsx
// Sprint 4c: simplified copy, practitioner type selector

import { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

// F11 fix (2 Jun): fetch a fresh Cognito token per request (Amplify auto-refreshes).
// This component does its own raw fetch (not via api.js), so it needs its own fresh-token helper.
async function freshToken() {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString();
}

const GOLD = '#C8A84B';
const DARK = '#0D1B2A';

const API = 'https://3z7hi3z1la.execute-api.ap-southeast-2.amazonaws.com/prod';

const fieldStyle = {
  width: '100%', fontSize: 13, fontFamily: "'DM Sans', sans-serif",
  border: '1px solid #E0DDD6', borderRadius: 7, padding: '10px 12px',
  background: '#FAFAF8', color: '#333', outline: 'none', boxSizing: 'border-box',
};

export async function checkUserOnboarded(token) {
  try {
    const t = await freshToken();
    const res = await fetch(`${API}/users/me`, {
      headers: { Authorization: `Bearer ${t}` }
    });
    if (!res.ok) return false;
    const data = await res.json();
    return data.practitioner_confirmed && data.tcs_accepted_at;
  } catch {
    return false;
  }
}

const PRACTITIONER_TYPES = [
  { value: 'solicitor_private',    label: 'Solicitor (private practice)' },
  { value: 'barrister',            label: 'Barrister' },
  { value: 'inhouse_counsel',      label: 'In-house counsel' },
  { value: 'government_solicitor', label: 'Government solicitor' },
  { value: 'support_staff',        label: 'Support staff (acting under legal practitioner supervision)' },
];

export default function Onboarding({ token, onComplete }) {
  const [practitionerType, setPractitionerType] = useState('solicitor_private');
  const [fullName,         setFullName]         = useState('');
  const [organisation,     setOrganisation]     = useState('');
  const [mobile,           setMobile]           = useState('');
  const [tcsAccepted,      setTcsAccepted]      = useState(false);
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState('');

  const handleSubmit = async () => {
    if (!tcsAccepted) return;
    if (!fullName.trim() || !organisation.trim() || !mobile.trim()) {
      setError('Please complete your name, organisation, and mobile number.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const t = await freshToken();
      const res = await fetch(`${API}/users/accept-tcs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${t}` },
        body: JSON.stringify({
          tcs_accepted: true, practitioner_confirmed: true, practitioner_type: practitionerType,
          full_name: fullName.trim(), organisation_name: organisation.trim(), mobile_number: mobile.trim(),
        })
      });
      if (!res.ok) throw new Error('Failed to record acceptance');
      onComplete();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#F7F5F0',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'DM Sans', system-ui, sans-serif", padding: 24,
    }}>
      <div style={{
        background: '#FFF', borderRadius: 16, padding: '48px 52px',
        maxWidth: 520, width: '100%', boxShadow: '0 4px 32px rgba(13,27,42,0.08)',
      }}>
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 11, color: '#BBB', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
            Calderbank.ai
          </div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, fontWeight: 500, color: DARK }}>
            Welcome to LitFit™
          </div>
        </div>

        <div style={{
          fontSize: 13, color: '#777', lineHeight: 1.7, marginBottom: 24,
          padding: '14px 16px', background: '#F7F5F0', borderRadius: 8,
          borderLeft: `3px solid ${GOLD}`,
        }}>
          LitFit™ is for solicitors (private practice and government), barristers,
          in-house counsel, and staff working under their supervision.
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#888', marginBottom: 8 }}>Full name</div>
          <input
            type="text" value={fullName} onChange={e => setFullName(e.target.value)}
            placeholder="e.g. Jane Citizen"
            style={fieldStyle}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#888', marginBottom: 8 }}>Organisation / firm / chambers</div>
          <input
            type="text" value={organisation} onChange={e => setOrganisation(e.target.value)}
            placeholder="e.g. your firm, company legal team, chambers, or agency"
            style={fieldStyle}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#888', marginBottom: 8 }}>Mobile number <span style={{ color: '#BBB', fontWeight: 400 }}>(for account security)</span></div>
          <input
            type="tel" value={mobile} onChange={e => setMobile(e.target.value)}
            placeholder="e.g. 04XX XXX XXX"
            style={fieldStyle}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#888', marginBottom: 8 }}>I am a:</div>
          <select
            value={practitionerType}
            onChange={e => setPractitionerType(e.target.value)}
            style={{
              width: '100%', fontSize: 13, fontFamily: "'DM Sans', sans-serif",
              border: '1px solid #E0DDD6', borderRadius: 7, padding: '10px 12px',
              background: '#FAFAF8', color: '#333', outline: 'none', cursor: 'pointer',
            }}
          >
            {PRACTITIONER_TYPES.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          padding: '12px 16px', marginBottom: 10, borderRadius: 8,
          border: `1px solid ${tcsAccepted ? GOLD : '#E8E4DC'}`,
          background: tcsAccepted ? 'rgba(200,168,75,0.05)' : '#FAFAF8',
          cursor: 'pointer', fontSize: 13, color: '#333', lineHeight: 1.55, transition: 'all 0.15s',
        }}>
          <input type="checkbox" checked={tcsAccepted} onChange={e => setTcsAccepted(e.target.checked)}
            style={{ marginTop: 2, accentColor: GOLD, flexShrink: 0 }} />
          <span>
            I accept the{' '}
            <a href="/terms.html" target="_blank" style={{ color: GOLD, textDecoration: 'none', fontWeight: 500 }}>Terms of Service</a>
            {' '}and{' '}
            <a href="/privacy.html" target="_blank" style={{ color: GOLD, textDecoration: 'none', fontWeight: 500 }}>Privacy Policy</a>.
          </span>
        </label>

        {error && <div style={{ color: '#D95050', fontSize: 12, marginBottom: 14 }}>{error}</div>}

        <button onClick={handleSubmit} disabled={!tcsAccepted || loading} style={{
          width: '100%', background: !tcsAccepted ? '#CCC' : DARK,
          color: '#FFF', border: 'none', padding: '13px 0', borderRadius: 9,
          fontSize: 14, fontFamily: "'DM Sans', sans-serif",
          cursor: !tcsAccepted ? 'default' : 'pointer', transition: 'background 0.15s', marginTop: 8,
        }}>
          {loading ? 'Saving…' : 'Continue to LitFit™ →'}
        </button>

        <div style={{ fontSize: 11, color: '#CCC', textAlign: 'center', marginTop: 20, lineHeight: 1.8 }}>
          Your matter data is stored in a secure, Australian-hosted environment (AWS Sydney,
          ap-southeast-2) — the same infrastructure used by Australian banks and government
          agencies. Each matter is kept entirely separate from other users' data. Documents
          you upload are processed by Claude via AWS Bedrock solely to generate your report —
          they are not retained by AWS or Anthropic, and are never used to train any AI model.
          Matter material and reports are automatically deleted 30 days after each run. Your
          data is held within a closed environment with strict access controls.<br/>
          © 2026 Calderbank AI Pty Limited. LitFit™ and Calderbank.ai™ used under licence.
        </div>
      </div>
    </div>
  );
}
